import { Drivers, Person, PolicyInformationDetails, Vehicles } from '@nationwide/dgs-internet-servicing-policy-requests';
import { DistributionPartnerAPI } from '@nationwide/internet-servicing-dpim-requests';
import { AutoDriver } from '../auto-driver';
import { CancellationDetailsForTPI } from './cancellationReasons';
import { Coverage } from './coverages-models';
import { MailingAddress } from './mailingAddress';
import { MortgageInformation } from './mortgage-company';
import { PolicyInformation } from './policy-information';
import { BillingMappingResponse, PropertyEndorsement } from './property-endorsement';
import { CoveredLocationResponse } from '../covered-locations';

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

export interface AutoPolicyData {
    policyNumber: string;
    policyInformationDetails?: PolicyInformationDetails;
    policyInformation?: PolicyInformation;
    cancellationDetailsForTPI?: CancellationDetailsForTPI;
    mailingAddress?: MailingAddress;
    person?: Person;
}

export interface AutoDriverData {
    drivers: Drivers[];
    autoDriver: AutoDriver;
}

export interface AutoVehicleData {
    vehicles: Vehicles[];
    coverages: Coverage[];
}

export interface AutoAgentData {
    agentInformation?: DistributionPartnerAPI.DpimRoleInfoResponse;
}

// ---------------------------------------------------------------------------
// Discriminated union members
// ---------------------------------------------------------------------------

/** Result shape returned by {@link PolicySearchService.getAutoPolicy}. */
export interface AutoSearchResult {
    readonly type: 'auto';
    policy: AutoPolicyData;
    drivers: AutoDriverData;
    vehicles: AutoVehicleData;
    agent: AutoAgentData;
    vin: string;
}

/** Result shape returned by {@link PolicySearchService.getPropertyPolicy}.
 *  The array indices mirror the `forkJoin` order in `PolicySearchResultsService`. */
export interface PropertySearchResult {
    readonly type: 'property';
    /** Index 0 – mapped policy information */
    policyInformation: {
        policyInformationDetails: PolicyInformationDetails | null;
        policyInformation: PolicyInformation | null;
        cancellationDetailsForTPI: CancellationDetailsForTPI | null;
        mailingAddress: MailingAddress | null;
        productType: string | null;
        policyNumber?: string;
        zipCode?: string;
    };
    /** Index 1 – primary named insured */
    insured: Person | null;
    /** Index 2 – mortgage information */
    mortgageInformation: MortgageInformation[] | null;
    /** Index 3 – property endorsements / coverages */
    propertyEndorsement: PropertyEndorsement | null;
    /** Index 4 – agent / distribution partner info */
    agentInformation: DistributionPartnerAPI.DpimRoleInfoResponse | null;
    /** Index 5 – covered location */
    coveredLocation: CoveredLocationResponse | null;
    /** Index 6 – billing details */
    billingDetails: BillingMappingResponse | null;
    /** Index 7 – renewal information */
    renewalInformation: unknown;
}

// ---------------------------------------------------------------------------
// Discriminated union + type guard helpers
// ---------------------------------------------------------------------------

/** Top-level discriminated union for all policy search result types. */
export type PolicySearchResult = AutoSearchResult | PropertySearchResult;

export function isAutoSearchResult(result: PolicySearchResult): result is AutoSearchResult {
    return result.type === 'auto';
}

export function isPropertySearchResult(result: PolicySearchResult): result is PropertySearchResult {
    return result.type === 'property';
}

// ---------------------------------------------------------------------------
// Utility: build a strongly-typed AutoSearchResult from the raw forkJoin tuple
// ---------------------------------------------------------------------------

export interface BuildAutoSearchResultOptions {
    policyInformationResponse: AutoPolicyData;
    driverInformationResponse: AutoDriverData;
    vehiclesAndCoveragesResponse: AutoVehicleData;
    agentResponse: DistributionPartnerAPI.DpimRoleInfoResponse | null;
    policyNumber: string;
    vin: string;
}

export function buildAutoSearchResult(options: BuildAutoSearchResultOptions): AutoSearchResult {
    const { policyInformationResponse, driverInformationResponse, vehiclesAndCoveragesResponse, agentResponse, policyNumber, vin } = options;
    return {
        type: 'auto',
        policy: { ...policyInformationResponse, policyNumber },
        drivers: driverInformationResponse,
        vehicles: vehiclesAndCoveragesResponse,
        agent: { agentInformation: agentResponse ?? undefined },
        vin
    };
}

// ---------------------------------------------------------------------------
// Utility: build a strongly-typed PropertySearchResult from the raw forkJoin array
// ---------------------------------------------------------------------------

export function buildPropertySearchResult(raw: unknown[]): PropertySearchResult {
    const POLICY_INFORMATION = 0;
    const INSURED = 1;
    const MORTGAGES = 2;
    const COVERAGES = 3;
    const AGENT = 4;
    const COVERED_LOCATION = 5;
    const BILLING = 6;
    const RENEWAL = 7;

    return {
        type: 'property',
        policyInformation: (raw[POLICY_INFORMATION] as PropertySearchResult['policyInformation']) ?? null,
        insured: (raw[INSURED] as Person) ?? null,
        mortgageInformation: (raw[MORTGAGES] as MortgageInformation[]) ?? null,
        propertyEndorsement: (raw[COVERAGES] as PropertyEndorsement) ?? null,
        agentInformation: (raw[AGENT] as DistributionPartnerAPI.DpimRoleInfoResponse) ?? null,
        coveredLocation: (raw[COVERED_LOCATION] as CoveredLocationResponse) ?? null,
        billingDetails: (raw[BILLING] as BillingMappingResponse) ?? null,
        renewalInformation: raw[RENEWAL] ?? null
    };
}
